import React, { ReactNode } from 'react';
import { BaseCard } from './BaseCard/BaseCard';
import { TagProps } from './Tag/Tag';
import { Footer, FooterProps } from './Footer/Footer';
import { PossiblyResponsiveValue, useResponsiveValue } from '../hooks/useResponsiveValue';
import { styles } from './Card.css';
import { LoadingSkeleton } from '../LoadingSkeleton/LoadingSkeleton';
import { Body } from './Body/Body';

type CardHeadingSize = 'large' | 'medium' | 'small';

export type CardProps = {
  cover: ReactNode;
  isContained?: boolean;
  isLoading?: boolean;
  showTag?: boolean;
  tagProps?: Omit<TagProps, 'className'>;
  footerProps?: string | FooterProps;
  heading?: string;
  headingSize?: CardHeadingSize;
  content?: string | ReactNode;
  maxContentLines?: number;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  href?: string;
  target?: string;
  width?: PossiblyResponsiveValue<React.CSSProperties['width']>;
};

export const Card = ({
  cover,
  isContained = true,
  tagProps,
  footerProps,
  heading,
  headingSize = 'small',
  content,
  maxContentLines = 3,
  onClick,
  href,
  target,
  isLoading = false,
  showTag = false,
  width: widthProp,
}: CardProps) => {
  const width = useResponsiveValue(widthProp);
  const isCoverOnly = !heading && !content && !footerProps;

  if (isLoading) {
    return <LoadingSkeleton.Card width={width} height="100%" isContained={isContained} shouldShimmer />;
  }

  return (
    <BaseCard
      style={{ width: width ? `${width}` : undefined }}
      className={styles.container}
      href={href}
      target={target}
      cover={cover}
      onClick={onClick}
      footer={
        !footerProps ? null : typeof footerProps === 'string' ? (
          <Footer leftContent={footerProps} />
        ) : (
          <Footer {...footerProps} />
        )
      }
      isContained={isContained}
    >
      {!isCoverOnly ? (
        <Body
          headingSize={headingSize}
          heading={heading}
          content={content}
          showTag={showTag}
          tagProps={tagProps}
          maxContentLines={maxContentLines}
        />
      ) : null}
    </BaseCard>
  );
};
